import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import SupplierService, { Supplier } from '../../services/supplier.service';
import { NumberTextField } from '../../components/common';

const SupplierForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState<Supplier>({
    name: '',
    code: '',
    contactPerson: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    country: '',
    taxNumber: '',
    paymentTerms: '',
    creditLimit: 0,
    notes: '',
    isActive: true,
    website: '',
  });

  useEffect(() => {
    if (isEdit && id) {
      fetchSupplier(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchSupplier = async (supplierId: number) => {
    try {
      setLoading(true);
      const response = await SupplierService.getOne(supplierId);
      const supplierData = response.data?.data;
      if (supplierData?.attributes) {
        setFormData(supplierData.attributes);
      }
    } catch (error) {
      console.error('Error fetching supplier:', error);
      setError('Failed to load supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const validateForm = (): boolean => {
    if (!formData.name) {
      setError('Company name is required');
      return false;
    }
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (isEdit && id) {
        await SupplierService.update(parseInt(id), formData);
        setSuccess('Supplier updated!');
      } else {
        await SupplierService.create(formData);
        setSuccess('Supplier created!');
      }

      setTimeout(() => {
        navigate('/suppliers');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving supplier:', error);
      setError(
        error.response?.data?.error?.message ||
          'Failed to save supplier. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Supplier' : 'New Supplier'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Company Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Supplier Code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  disabled={isEdit}
                  helperText="Auto-generated if left empty"
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  required
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Address Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Address Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Business Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Business Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Tax Number / VAT ID"
                  name="taxNumber"
                  value={formData.taxNumber}
                  onChange={handleChange}
                />
                <NumberTextField
                  fullWidth
                  label="Credit Limit"
                  name="creditLimit"
                  value={formData.creditLimit || ''}
                  onChange={handleChange}
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="In OMR"
                />
              </Stack>

              <TextField
                fullWidth
                label="Website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://example.com"
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Payment Terms"
                name="paymentTerms"
                value={formData.paymentTerms}
                onChange={handleChange}
                placeholder="e.g., Net 30, Net 60, etc."
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Additional notes about the supplier..."
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={formData.isActive !== false}
                    onChange={handleChange}
                  />
                }
                label="Active Supplier"
              />
            </Stack>
          </Paper>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/suppliers')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Supplier' : 'Create Supplier'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default SupplierForm;

